

import { Button, Alert, FormField, TextInput, SuccessNotice } from "@a2z/ui";

import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateAdminPasswordMutation } from "../services/useUpdateAdminPasswordMutation";
import { getRequiredFieldErrors } from "@/shared/components/forms/validation";

export function PasswordForm({ adminId }: { adminId: string }) {
  const t = useTranslations();
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const { mutateAsync: updatePassword } = useUpdateAdminPasswordMutation();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(false);
    setFieldErrors({});
    const formData = new FormData(event.currentTarget);
    const requiredErrors = getRequiredFieldErrors(
      formData,
      ["password"],
      t(keys.errors.required)
    );

    if (Object.keys(requiredErrors).length > 0) {
      setFieldErrors(requiredErrors);
      return;
    }

    try {
      await updatePassword({
        adminId,
        password: String(formData.get("password") ?? ""),
      });
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        setError(error.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      return;
    }

    setSuccess(true);
  };

  return (
    <form className="mx-auto flex w-full max-w-[640px] flex-col gap-4" onSubmit={handleSubmit}>
      {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
      {success ? (
        <SuccessNotice title={t(keys.common.updateSuccess)} />
      ) : null}
      <FormField
        label={t(keys.common.password)}
        name="password"
        required
        error={fieldErrors.password}
      >
        <TextInput
          name="password"
          type="password"
          placeholder={t(keys.placeholders.password)}
          required
          hasError={Boolean(fieldErrors.password)}
        />
      </FormField>
      <Button
        type="submit"
        variant="primary" className="h-11 w-full rounded-lg px-4 text-sm font-semibold transition"
      >
        {t(keys.common.update)}
      </Button>
    </form>
  );
}
